import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const SessionFilters = ({ 
  onFilterChange = () => {},
  onSearch = () => {},
  onRefresh = () => {},
  totalSessions = 0,
  activeSessions = 0
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilters, setActiveFilters] = useState({
    status: 'all',
    timeRange: 'today',
    participants: 'all'
  });
  const [showAdvanced, setShowAdvanced] = useState(false);

  const statusFilters = [
    { value: 'all', label: 'Todas', count: totalSessions },
    { value: 'active', label: 'Activas', count: activeSessions },
    { value: 'payment-pending', label: 'Pago Pendiente', count: 3 },
    { value: 'completed', label: 'Completadas', count: 12 },
    { value: 'expired', label: 'Expiradas', count: 2 },
    { value: 'disputed', label: 'En Disputa', count: 1 }
  ];

  const timeRangeFilters = [
    { value: 'today', label: 'Hoy' },
    { value: 'yesterday', label: 'Ayer' },
    { value: 'week', label: 'Esta Semana' },
    { value: 'month', label: 'Este Mes' }
  ];

  const participantFilters = [
    { value: 'all', label: 'Todos' },
    { value: '1-2', label: '1-2 personas' },
    { value: '3-5', label: '3-5 personas' },
    { value: '6+', label: '6+ personas' }
  ];

  const handleSearchChange = (e) => {
    const value = e?.target?.value;
    setSearchTerm(value);
    onSearch(value);
  };

  const handleFilterChange = (filterType, value) => {
    const newFilters = {
      ...activeFilters,
      [filterType]: value
    };
    setActiveFilters(newFilters);
    onFilterChange(newFilters);
  };

  const clearAllFilters = () => {
    const defaultFilters = {
      status: 'all',
      timeRange: 'today',
      participants: 'all'
    };
    setActiveFilters(defaultFilters);
    setSearchTerm('');
    onFilterChange(defaultFilters);
    onSearch('');
  };

  const hasActiveFilters = activeFilters?.status !== 'all' || 
                          activeFilters?.timeRange !== 'today' || 
                          activeFilters?.participants !== 'all' ||
                          searchTerm?.length > 0;

  return (
    <div className="bg-surface border border-border rounded-lg p-4 space-y-4">
      {/* Search and Quick Actions */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <Input
            type="search"
            placeholder="Buscar por mesa, ID de sesión o participante..."
            value={searchTerm}
            onChange={handleSearchChange}
            className="w-full"
          />
        </div>
        
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={onRefresh}
            iconName="RefreshCw"
            iconSize={16}
          >
            Actualizar
          </Button>
          
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowAdvanced(!showAdvanced)}
            iconName={showAdvanced ? "ChevronUp" : "ChevronDown"}
            iconSize={16}
          >
            Filtros Avanzados
          </Button>
          
          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearAllFilters}
              iconName="X"
              iconSize={16}
            >
              Limpiar
            </Button>
          )}
        </div>
      </div>
      {/* Status Filter Tabs */}
      <div className="flex flex-wrap gap-2">
        {statusFilters?.map((filter) => (
          <Button
            key={filter?.value}
            variant={activeFilters?.status === filter?.value ? "default" : "outline"}
            size="sm"
            onClick={() => handleFilterChange('status', filter?.value)}
            className="text-sm"
          >
            {filter?.label}
            {filter?.count !== undefined && (
              <span className="ml-1 text-xs opacity-75">({filter?.count})</span>
            )}
          </Button>
        ))}
      </div>
      {/* Advanced Filters */}
      {showAdvanced && (
        <div className="pt-4 border-t border-border">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Time Range Filter */}
            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">
                Período de Tiempo
              </label>
              <div className="flex flex-wrap gap-1">
                {timeRangeFilters?.map((filter) => (
                  <Button
                    key={filter?.value}
                    variant={activeFilters?.timeRange === filter?.value ? "default" : "ghost"}
                    size="xs"
                    onClick={() => handleFilterChange('timeRange', filter?.value)}
                  >
                    {filter?.label}
                  </Button>
                ))}
              </div>
            </div>

            {/* Participants Filter */}
            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">
                Número de Participantes
              </label>
              <div className="flex flex-wrap gap-1">
                {participantFilters?.map((filter) => (
                  <Button
                    key={filter?.value}
                    variant={activeFilters?.participants === filter?.value ? "default" : "ghost"}
                    size="xs"
                    onClick={() => handleFilterChange('participants', filter?.value)}
                  >
                    {filter?.label}
                  </Button>
                ))}
              </div>
            </div>
          </div>

          {/* Additional Options */}
          <div className="mt-4 pt-4 border-t border-border">
            <div className="flex flex-wrap gap-2">
              <Button
                variant="ghost"
                size="xs"
                iconName="AlertTriangle"
                iconSize={14}
              >
                Solo Problemas
              </Button>
              <Button
                variant="ghost"
                size="xs"
                iconName="Clock"
                iconSize={14}
              >
                Sesiones Largas (&gt;2h)
              </Button>
              <Button
                variant="ghost"
                size="xs"
                iconName="DollarSign"
                iconSize={14}
              >
                Cuentas Altas (&gt;€100)
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* Active Filters Summary */}
      {hasActiveFilters && (
        <div className="flex items-center space-x-2 text-sm text-text-secondary">
          <Icon name="Filter" size={14} />
          <span>Filtros activos:</span>
          {activeFilters?.status !== 'all' && (
            <span className="px-2 py-1 bg-primary/10 text-primary rounded-full text-xs">
              Estado: {statusFilters?.find(f => f?.value === activeFilters?.status)?.label}
            </span>
          )}
          {activeFilters?.timeRange !== 'today' && (
            <span className="px-2 py-1 bg-primary/10 text-primary rounded-full text-xs">
              Tiempo: {timeRangeFilters?.find(f => f?.value === activeFilters?.timeRange)?.label}
            </span>
          )}
          {activeFilters?.participants !== 'all' && (
            <span className="px-2 py-1 bg-primary/10 text-primary rounded-full text-xs">
              Participantes: {participantFilters?.find(f => f?.value === activeFilters?.participants)?.label}
            </span>
          )}
          {searchTerm && (
            <span className="px-2 py-1 bg-primary/10 text-primary rounded-full text-xs">
              Búsqueda: "{searchTerm}"
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default SessionFilters;